<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import orderIssuesAPI from 'dashboard/api/orderIssues';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'success']);

const { t } = useI18n();

// State
const loading = ref(false);
const saving = ref(false);
const problemCategories = ref([]);
const actionCategories = ref([]);
const unassignedTags = ref({ problem: [], action: [] });
const availableTags = ref({ problem_tags: [], action_tags: [] });

// Edit state
const editingCategory = ref(null);
const showEditForm = ref(false);
const editForm = ref({
  id: null,
  name: '',
  key: '',
  category_type: 'problem',
  description: '',
  tags: [],
  include_in_rates: false,
  rate_key: '',
});

// Active tab
const activeTab = ref('problem');

const currentCategories = computed(() => 
  activeTab.value === 'problem' ? problemCategories.value : actionCategories.value
);

const currentUnassignedTags = computed(() => 
  unassignedTags.value[activeTab.value] || []
);

const currentAvailableTags = computed(() => 
  activeTab.value === 'problem' ? availableTags.value.problem_tags : availableTags.value.action_tags
);

// Fetch categories
const fetchCategories = async () => {
  loading.value = true;
  try {
    const [categoriesRes, tagsRes] = await Promise.all([
      orderIssuesAPI.getMetricCategories(),
      orderIssuesAPI.getAvailableTags(),
    ]);
    
    problemCategories.value = categoriesRes.data.problem_categories || [];
    actionCategories.value = categoriesRes.data.action_categories || [];
    unassignedTags.value = categoriesRes.data.unassigned_tags || { problem: [], action: [] };
    availableTags.value = tagsRes.data || { problem_tags: [], action_tags: [] };
  } catch (error) {
    useAlert(t('ORDER_ISSUES.METRIC_CATEGORIES.FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

// Open edit form
const openEditForm = (category = null) => {
  if (category) {
    editForm.value = {
      id: category.id,
      name: category.name,
      key: category.key,
      category_type: category.category_type,
      description: category.description || '',
      tags: [...category.tags],
      include_in_rates: category.include_in_rates || false,
      rate_key: category.rate_key || '',
    };
  } else {
    editForm.value = {
      id: null,
      name: '',
      key: '',
      category_type: activeTab.value,
      description: '',
      tags: [],
      include_in_rates: false,
      rate_key: '',
    };
  }
  editingCategory.value = category;
  showEditForm.value = true;
};

// Close edit form
const closeEditForm = () => {
  showEditForm.value = false;
  editingCategory.value = null;
};

// Generate key from name
const generateKey = () => {
  editForm.value.key = editForm.value.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

// Toggle tag in form
const toggleTagInForm = (tag) => {
  const index = editForm.value.tags.indexOf(tag);
  if (index === -1) {
    editForm.value.tags.push(tag);
  } else {
    editForm.value.tags.splice(index, 1);
  }
};

// Save category
const saveCategory = async () => {
  if (!editForm.value.name || !editForm.value.key) {
    useAlert(t('ORDER_ISSUES.METRIC_CATEGORIES.VALIDATION_ERROR'));
    return;
  }

  saving.value = true;
  try {
    const payload = {
      name: editForm.value.name,
      key: editForm.value.key,
      category_type: editForm.value.category_type,
      description: editForm.value.description,
      tags: editForm.value.tags,
      include_in_rates: editForm.value.include_in_rates,
      rate_key: editForm.value.rate_key,
    };

    if (editForm.value.id) {
      await orderIssuesAPI.updateMetricCategory(editForm.value.id, payload);
      useAlert(t('ORDER_ISSUES.METRIC_CATEGORIES.UPDATE_SUCCESS'));
    } else {
      await orderIssuesAPI.createMetricCategory(payload);
      useAlert(t('ORDER_ISSUES.METRIC_CATEGORIES.CREATE_SUCCESS'));
    }
    
    closeEditForm();
    await fetchCategories();
    emit('success');
  } catch (error) {
    useAlert(t('ORDER_ISSUES.METRIC_CATEGORIES.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

// Delete category
const deleteCategory = async (category) => {
  if (!confirm(t('ORDER_ISSUES.METRIC_CATEGORIES.DELETE_CONFIRM', { name: category.name }))) {
    return;
  }

  try {
    await orderIssuesAPI.deleteMetricCategory(category.id);
    useAlert(t('ORDER_ISSUES.METRIC_CATEGORIES.DELETE_SUCCESS'));
    await fetchCategories();
    emit('success');
  } catch (error) {
    useAlert(t('ORDER_ISSUES.METRIC_CATEGORIES.DELETE_ERROR'));
  }
};

// Reset to defaults
const resetToDefaults = async () => {
  if (!confirm(t('ORDER_ISSUES.METRIC_CATEGORIES.RESET_CONFIRM'))) {
    return;
  }

  loading.value = true;
  try {
    await orderIssuesAPI.resetCategoriesToDefaults();
    useAlert(t('ORDER_ISSUES.METRIC_CATEGORIES.RESET_SUCCESS'));
    await fetchCategories();
    emit('success');
  } catch (error) {
    useAlert(t('ORDER_ISSUES.METRIC_CATEGORIES.RESET_ERROR'));
  } finally {
    loading.value = false;
  }
};

// Close modal
const closeModal = () => {
  closeEditForm();
  emit('close');
};

// Seed defaults if no categories
const seedDefaultsIfEmpty = async () => {
  if (problemCategories.value.length === 0 && actionCategories.value.length === 0) {
    try {
      await orderIssuesAPI.seedDefaultCategories();
      await fetchCategories();
    } catch {
      // Ignore if already seeded
    }
  }
};

// Watch for modal open
watch(() => props.show, async (newVal) => {
  if (newVal) {
    await fetchCategories();
    await seedDefaultsIfEmpty();
  }
});
</script>

<template>
  <Modal
    :show="show"
    size="large"
    :show-close-button="false"
    :on-close="closeModal"
  >
    <div class="flex flex-col h-[70vh] max-h-[600px]">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak">
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ $t('ORDER_ISSUES.METRIC_CATEGORIES.TITLE') }}
        </h2>
        <button
          class="text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @click="closeModal"
        >
          <i class="i-lucide-x text-lg" />
        </button>
      </div>

      <!-- Tabs -->
      <div class="flex items-center gap-4 px-6 py-3 border-b border-n-weak">
        <button
          class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
          :class="activeTab === 'problem' 
            ? 'bg-n-brand text-white' 
            : 'text-n-slate-11 hover:bg-n-alpha-1'"
          @click="activeTab = 'problem'"
        >
          {{ $t('ORDER_ISSUES.METRIC_CATEGORIES.PROBLEM_CATEGORIES') }}
        </button>
        <button
          class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
          :class="activeTab === 'action' 
            ? 'bg-n-brand text-white' 
            : 'text-n-slate-11 hover:bg-n-alpha-1'"
          @click="activeTab = 'action'"
        >
          {{ $t('ORDER_ISSUES.METRIC_CATEGORIES.ACTION_CATEGORIES') }}
        </button>
        <div class="flex-1" />
        <button
          class="text-xs text-n-slate-11 hover:text-n-slate-12 underline"
          @click="resetToDefaults"
        >
          {{ $t('ORDER_ISSUES.METRIC_CATEGORIES.RESET_TO_DEFAULTS') }}
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto px-6 py-4">
        <!-- Loading -->
        <div v-if="loading" class="flex items-center justify-center py-8">
          <i class="i-lucide-loader-2 animate-spin text-2xl text-n-slate-11" />
        </div>

        <!-- Edit Form -->
        <div v-else-if="showEditForm" class="space-y-4">
          <div class="flex items-center gap-2 mb-4">
            <button
              class="text-n-slate-11 hover:text-n-slate-12"
              @click="closeEditForm"
            >
              <i class="i-lucide-arrow-left" />
            </button>
            <h3 class="text-base font-medium text-n-slate-12">
              {{ editForm.id 
                ? $t('ORDER_ISSUES.METRIC_CATEGORIES.EDIT_CATEGORY') 
                : $t('ORDER_ISSUES.METRIC_CATEGORIES.NEW_CATEGORY') }}
            </h3>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-n-slate-11 mb-1">
                {{ $t('ORDER_ISSUES.METRIC_CATEGORIES.FORM.NAME') }}
              </label>
              <input
                v-model="editForm.name"
                type="text"
                class="no-margin w-full px-3 py-2 text-sm border border-n-weak rounded-md bg-n-background"
                @blur="!editForm.id && generateKey()"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-n-slate-11 mb-1">
                {{ $t('ORDER_ISSUES.METRIC_CATEGORIES.FORM.KEY') }}
              </label>
              <input
                v-model="editForm.key"
                type="text"
                class="no-margin w-full px-3 py-2 text-sm border border-n-weak rounded-md bg-n-background"
                :disabled="!!editForm.id"
              />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">
              {{ $t('ORDER_ISSUES.METRIC_CATEGORIES.FORM.DESCRIPTION') }}
            </label>
            <textarea
              v-model="editForm.description"
              rows="2"
              class="w-full px-3 py-2 text-sm border border-n-weak rounded-md bg-n-background resize-none"
            />
          </div>

          <div class="flex items-center gap-4">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="editForm.include_in_rates"
                type="checkbox"
                class="w-4 h-4 accent-n-brand"
              />
              <span class="text-sm text-n-slate-12">
                {{ $t('ORDER_ISSUES.METRIC_CATEGORIES.FORM.INCLUDE_IN_RATES') }}
              </span>
            </label>
            <div v-if="editForm.include_in_rates" class="flex-1">
              <input
                v-model="editForm.rate_key"
                type="text"
                class="no-margin w-full px-3 py-2 text-sm border border-n-weak rounded-md bg-n-background"
                :placeholder="$t('ORDER_ISSUES.METRIC_CATEGORIES.FORM.RATE_KEY_PLACEHOLDER')"
              />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-2">
              {{ $t('ORDER_ISSUES.METRIC_CATEGORIES.FORM.TAGS') }}
            </label>
            <div class="flex flex-wrap gap-2 p-3 border border-n-weak rounded-md bg-n-alpha-1 max-h-48 overflow-y-auto">
              <button
                v-for="tag in currentAvailableTags"
                :key="tag"
                class="px-2 py-1 text-xs rounded-full transition-colors"
                :class="editForm.tags.includes(tag) 
                  ? 'bg-n-brand text-white' 
                  : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'"
                @click="toggleTagInForm(tag)"
              >
                {{ tag }}
              </button>
              <span v-if="currentAvailableTags.length === 0" class="text-sm text-n-slate-10 italic">
                {{ $t('ORDER_ISSUES.METRIC_CATEGORIES.NO_TAGS_AVAILABLE') }}
              </span>
            </div>
          </div>

          <div class="flex justify-end gap-2 pt-4 border-t border-n-weak">
            <Button
              :label="$t('ORDER_ISSUES.METRIC_CATEGORIES.FORM.CANCEL')"
              variant="secondary"
              size="sm"
              @click="closeEditForm"
            />
            <Button
              :label="$t('ORDER_ISSUES.METRIC_CATEGORIES.FORM.SAVE')"
              size="sm"
              :loading="saving"
              @click="saveCategory"
            />
          </div>
        </div>

        <!-- Categories List -->
        <div v-else class="space-y-4">
          <!-- Add New Button -->
          <button
            class="flex items-center gap-2 w-full px-4 py-3 text-sm text-n-brand border border-dashed border-n-brand rounded-lg hover:bg-n-brand/5 transition-colors"
            @click="openEditForm()"
          >
            <i class="i-lucide-plus" />
            {{ $t('ORDER_ISSUES.METRIC_CATEGORIES.ADD_CATEGORY') }}
          </button>

          <!-- Categories -->
          <div
            v-for="category in currentCategories"
            :key="category.id"
            class="p-4 border border-n-weak rounded-lg"
          >
            <div class="flex items-start justify-between mb-2">
              <div>
                <h4 class="font-medium text-n-slate-12">{{ category.name }}</h4>
                <p class="text-xs text-n-slate-10">{{ category.key }}</p>
              </div>
              <div class="flex items-center gap-1">
                <button
                  class="p-1.5 text-n-slate-11 hover:text-n-brand transition-colors"
                  @click="openEditForm(category)"
                >
                  <i class="i-lucide-pencil text-sm" />
                </button>
                <button
                  class="p-1.5 text-n-slate-11 hover:text-red-500 transition-colors"
                  @click="deleteCategory(category)"
                >
                  <i class="i-lucide-trash-2 text-sm" />
                </button>
              </div>
            </div>
            
            <p v-if="category.description" class="text-sm text-n-slate-11 mb-2">
              {{ category.description }}
            </p>
            
            <div class="flex flex-wrap gap-1">
              <span
                v-for="tag in category.tags"
                :key="tag"
                class="px-2 py-0.5 text-xs bg-n-alpha-2 text-n-slate-11 rounded-full"
              >
                {{ tag }}
              </span>
              <span v-if="category.tags.length === 0" class="text-xs text-n-slate-10 italic">
                {{ $t('ORDER_ISSUES.METRIC_CATEGORIES.NO_TAGS') }}
              </span>
            </div>

            <div v-if="category.include_in_rates" class="mt-2 pt-2 border-t border-n-weak">
              <span class="text-xs text-n-brand">
                <i class="i-lucide-chart-bar inline-block mr-1" />
                {{ $t('ORDER_ISSUES.METRIC_CATEGORIES.INCLUDED_IN_RATES') }}: {{ category.rate_key }}
              </span>
            </div>
          </div>

          <!-- Unassigned Tags -->
          <div v-if="currentUnassignedTags.length > 0" class="mt-6">
            <h4 class="text-sm font-medium text-n-slate-11 mb-2">
              {{ $t('ORDER_ISSUES.METRIC_CATEGORIES.UNASSIGNED_TAGS') }}
            </h4>
            <div class="flex flex-wrap gap-1 p-3 border border-dashed border-n-weak rounded-lg bg-amber-50">
              <span
                v-for="tag in currentUnassignedTags"
                :key="tag"
                class="px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded-full"
              >
                {{ tag }}
              </span>
            </div>
            <p class="text-xs text-n-slate-10 mt-1">
              {{ $t('ORDER_ISSUES.METRIC_CATEGORIES.UNASSIGNED_HINT') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>
